import {DestroyRef, inject, Injectable} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {catchError, combineLatest, EMPTY, filter, first, map, Observable, of, switchMap, throwError,} from 'rxjs';
import {NOT_FOUND_LABEL} from 'src/app/config/app.config';
import {Account} from 'src/app/core/entities/account/domain/model/account.model';
import {AccountStorePort} from 'src/app/core/entities/account/domain/ports/account-store.port';
import {ACCOUNT_STORE} from 'src/app/core/entities/account/domain/tokens/account-store.token';
import {EVENT_BUFFER_PROTOCOL} from 'src/app/core/entities/event-buffer/enums/event-protocol.enum';
import {
  OrganizationRepositoryAdapter
} from 'src/app/core/entities/organization/infrastructure/adapters/organization-repository.adapter';
import {
  SESSION_STORAGE_REPOSITORY
} from 'src/app/core/entities/session/infrastructure/interfaces/session-storage.repository';
import {SOCKET_AUTHOR_MODEL} from 'src/app/core/enums/events-buffer/socket/socket-author-model.enum';
import {SOCKET_TRACKING_EVENT} from 'src/app/core/enums/events-buffer/socket/socket-tracking-event.enum';
import {Logger} from 'src/app/core/interfaces/Logger';
import {IOrganization} from 'src/app/core/entities/organization/domain/interfaces/organization.interface';
import {ISocketPayload} from 'src/app/core/interfaces/socket-payload.interface';
import {AppContextService} from 'src/app/shared/services/app-context/app-context.service';
import {generateIndexDBKey} from 'src/app/shared/utils/indexdb.utils';
import {SocketService} from '../../socket/socket.service';
import {TRACKER_TYPE} from '../enums/tracker.enum';
import {ITrackerAccountBody} from '../interfaces/account-tracker.interface';
import {TrackerImpl} from '../interfaces/tracker.model';
import {ITrackingRecord} from '../interfaces/tracking-record.interface';
import {TrackingService} from '../tracking.service';
import {SERVICE_STATE_SERVICE} from '@features/service/domain/tokens/service-state.token';

/**
 * Service responsible for tracking account-related events in the application.
 * Handles adding, removing, and sending tracking data for accounts.
 */
@Injectable({
  providedIn: 'root',
})
export class AccountTrackerService {
  private readonly logger = inject(Logger);
  private readonly socketService = inject(SocketService);
  private readonly appContextService = inject(AppContextService);
  private readonly organizationService = inject(OrganizationRepositoryAdapter);
  private readonly trackingService = inject(TrackingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly sessionStorageRepository = inject(SESSION_STORAGE_REPOSITORY);
  private readonly accountStoreService = inject<AccountStorePort>(ACCOUNT_STORE);

  private readonly serviceStateService = inject(SERVICE_STATE_SERVICE);

  private readonly tracker: TrackerImpl<ITrackerAccountBody[]> =
    new TrackerImpl<ITrackerAccountBody[]>();

  /**
   * Initializes the service by setting up event listeners for adding,
   * removing, and sending tracking data.
   */
  constructor() {
    this.initAddListener();
    this.initRemoveListener();
    this.initSendListener();
  }

  /**
   * Sets up a listener for add track events specific to account tracking.
   * When an account tracking record is received, it adds the tracking data.
   */
  private initAddListener(): void {
    this.trackingService.onAddTrack$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (record): record is ITrackingRecord<ITrackerAccountBody> =>
            record.type === TRACKER_TYPE.account
        )
      )
      .subscribe((record) => {
        if (record.body) {
          this.add(record.body);
        }
      });
  }

  /**
   * Sets up a listener for remove track events specific to account tracking.
   * When a remove event is received, it removes the current account's tracking data.
   */
  private initRemoveListener(): void {
    this.trackingService.onRemoveTrack$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (record): record is ITrackingRecord<ITrackerAccountBody> =>
            record.type === TRACKER_TYPE.account
        )
      )
      .subscribe((record) => {
        this.remove(record.trackId);
      });
  }

  /**
   * Sets up a listener for send track events specific to account tracking.
   * When a send event is received, it sends the collected tracking data.
   */
  private initSendListener(): void {
    this.trackingService.onSendTrack$
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        filter(
          (record): record is ITrackingRecord<ITrackerAccountBody> =>
            record.type === TRACKER_TYPE.account
        )
      )
      .subscribe((record) => {
        this.send(record.trackId);
      });
  }

  /**
   * Adds tracking data for the current account.
   * Retrieves the current account, gets any existing track data for the account,
   * adds the new data with a timestamp, and updates the tracker.
   *
   * @param body - The tracking data to add
   */
  private add(body: ITrackerAccountBody): void {
    const session$ = this.sessionStorageRepository.get();
    const account$ = this.accountStoreService.getSelectedAccount$().pipe(
      filter((account): account is Account => !!account)
    );

    combineLatest([session$, account$])
      .pipe(
        first(),
        takeUntilDestroyed(this.destroyRef),
        switchMap(([session, account]) => {
          if (!session) {
            throw new Error('Session not found');
          }

          if (!account.trackId) {
            throw new Error('Current account trackId is empty');
          }

          return this.tracker.getTrack(account.trackId).pipe(
            map(track => {
              const previousEvents = track?.body ?? [];
              const newEvent = { ...body, timestamp: Date.now() };

              return [...previousEvents, newEvent];
            }),
            switchMap(updatedBody =>
              this.tracker.addTrack(account.trackId, TRACKER_TYPE.account, updatedBody)
            )
          );
        }),
        catchError(error => {
          this.logger.error(error, `[AccountTrackerService][add] ${error.message}`);

          return EMPTY;
        })
      )
      .subscribe();
  }

  /**
   * Removes tracking data for the current account.
   * Gets the current account and removes its associated tracking data.
   *
   * @param specificTrackId - Optional specific trackId to use instead of current account's trackId
   */
  private remove(specificTrackId?: string): void {
    this.sessionStorageRepository
      .get()
      .pipe(
        first(),
        switchMap((session) => {
          if (!session) {
            return throwError(() =>
              this.logger.error(
                new Error(
                  `[AccountTrackerService][remove] current session not found`
                )
              )
            );
          }

          const trackId = specificTrackId ?? this.accountStoreService.getSelectedAccount()?.trackId ?? '';

          return this.tracker.removeTrack(trackId);
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Sends the collected tracking data for the current account through the socket service.
   * Prepares the payload with account information, organization data, waiter details,
   * and the tracking data before emitting the event.
   *
   * @param specificTrackId - Optional specific trackId to use instead of current account's trackId
   */
  private send(specificTrackId?: string): void {
    this.getPayloadData()
      .pipe(
        first(),
        switchMap((payloadData) => {
          const trackId = specificTrackId ?? this.accountStoreService.getSelectedAccount()?.trackId ?? '';

          return combineLatest({
            payloadData: of(payloadData),
            track: this.tracker.getTrack(trackId),
          });
        }),
        switchMap((track) => {
          const worker = this.serviceStateService.getCurrentWorker();
          const currentAccount = this.accountStoreService.getSelectedAccount();

          const payload: ISocketPayload = {
            negocio_id: currentAccount?.negocio_id ?? NOT_FOUND_LABEL,
            action: SOCKET_TRACKING_EVENT.create,
            customData: TRACKER_TYPE.account,
            author: {
              authorId: worker?.id ?? NOT_FOUND_LABEL,
              model: SOCKET_AUTHOR_MODEL.waiter,
            },
            device: this.appContextService.deviceName,
            organization: this.appContextService.organizationName,
            serviceId: this.appContextService.serviceId,
            timestamp: Date.now(),
            payload: {
              track,
            },
          };

          return this.socketService.prepareToEmitEvent({
            _id: generateIndexDBKey(),
            name: SOCKET_TRACKING_EVENT.create,
            payload,
            protocol: EVENT_BUFFER_PROTOCOL.socket,
          });
        }),
        takeUntilDestroyed(this.destroyRef)
      )
      .subscribe();
  }

  /**
   * Gathers data needed for the tracking payload.
   * Retrieves the current organization and account information.
   *
   * @returns An Observable with the organization and current account data
   */
  private getPayloadData(): Observable<IOrganization | null> {
    return this.organizationService.getCurrentOrganization().pipe(first());
  }
}
